import { Copy, Download, Edit3, GripVertical, Plus, Repeat, Trash2, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import EmptyState from "../../components/EmptyState.jsx";
import { Field, Toggle } from "../../components/FormControls.jsx";
import Modal from "../../components/Modal.jsx";
import SearchBar from "../../components/SearchBar.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import StatusChip from "../../components/StatusChip.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import {
  copyRoutinesBetweenDays,
  bulkChangeRoutineRoom,
  deleteRoutine,
  duplicateRoutine,
  getMasterData,
  getRoutines,
  importRoutines,
  reorderRoutines,
  saveRoutine
} from "../../firebase/firestore.js";
import { DAYS, getDayName } from "../../utils/date.js";
import { exportRowsToExcel, readExcelFile } from "../../utils/export.js";
import { timeRangeLabel } from "../../utils/time.js";
import { NA_ROOM_ID, enrichRoutine, routineToExportRow } from "./adminUtils.js";

const emptyRoutine = {
  days: [getDayName()],
  classId: "",
  sectionId: "",
  roomId: NA_ROOM_ID,
  roomNumber: "N/A",
  subjectId: "",
  teacherId: "",
  scheduledIn: "07:00",
  scheduledOut: "08:00",
  status: "active",
  active: true,
  notes: ""
};

const emptyFilters = {
  day: "",
  classId: "",
  roomId: "",
  teacherId: "",
  subjectId: ""
};

const WEEKDAY_DAYS = DAYS.filter((day) => day !== "Sunday");

function normalizedRoutineValue(value) {
  return JSON.stringify({
    days: [...(value.days || [])].sort(),
    classId: value.classId || "",
    sectionId: value.sectionId || "",
    roomId: value.roomId || "",
    roomNumber: value.roomNumber || "",
    subjectId: value.subjectId || "",
    teacherId: value.teacherId || "",
    scheduledIn: value.scheduledIn || "",
    scheduledOut: value.scheduledOut || "",
    status: value.status || "active",
    active: value.active !== false,
    notes: value.notes || ""
  });
}

function getTeachersForSubject(teachers, subjectId) {
  if (!subjectId) return [];
  return teachers.filter((teacher) => {
    const subjectIds = Array.isArray(teacher.subjectIds) ? teacher.subjectIds : [];
    return subjectIds.includes(subjectId) || teacher.subjectId === subjectId;
  });
}

function SelectOptions({ rows, labelKey = "name" }) {
  return rows.map((row) => (
    <option key={row.id} value={row.id}>
      {row[labelKey]}
    </option>
  ));
}

function updateRoomValue(setter, nextRoomId) {
  setter((value) => ({
    ...value,
    roomId: nextRoomId || NA_ROOM_ID,
    roomNumber: nextRoomId === NA_ROOM_ID || !nextRoomId ? "N/A" : ""
  }));
}

function RoutineForm({ form, setForm, master }) {
  const sections = (master.sections || []).filter((section) => !form.classId || section.classId === form.classId);
  const teachers = getTeachersForSubject(master.teachers || [], form.subjectId);
  const allWeekdaysSelected = WEEKDAY_DAYS.every((day) => form.days?.includes(day));

  return (
    <>
      <div className="checkbox-panel">
        <span>Days</span>
        <label className="select-all-row">
          <input
            type="checkbox"
            checked={allWeekdaysSelected}
            onChange={(event) => {
              setForm((value) => ({
                ...value,
                days: event.target.checked ? WEEKDAY_DAYS : []
              }));
            }}
          />
          Select all days except Sunday
        </label>
        {DAYS.map((day) => (
          <label key={day}>
            <input
              type="checkbox"
              checked={form.days?.includes(day) || false}
              onChange={(event) => {
                const current = form.days || [];
                const days = event.target.checked ? [...current, day] : current.filter((item) => item !== day);
                setForm((value) => ({ ...value, days }));
              }}
            />
            {day}
          </label>
        ))}
      </div>
      <div className="form-grid two">
        <Field label="Class">
          <select value={form.classId} onChange={(event) => setForm((value) => ({ ...value, classId: event.target.value, sectionId: "" }))} required>
            <option value="">Select class</option>
            <SelectOptions rows={master.classes || []} />
          </select>
        </Field>
        <Field label="Section">
          <select value={form.sectionId} onChange={(event) => setForm((value) => ({ ...value, sectionId: event.target.value }))} required>
            <option value="">Select section</option>
            <SelectOptions rows={sections} />
          </select>
        </Field>
        <Field label="Room">
          <select value={form.roomId || NA_ROOM_ID} onChange={(event) => updateRoomValue(setForm, event.target.value)} required>
            <option value={NA_ROOM_ID}>N/A</option>
            <SelectOptions rows={master.rooms || []} labelKey="roomNumber" />
          </select>
        </Field>
        <Field label="Subject">
          <select value={form.subjectId} onChange={(event) => setForm((value) => ({ ...value, subjectId: event.target.value, teacherId: "" }))} required>
            <option value="">Select subject</option>
            <SelectOptions rows={master.subjects || []} />
          </select>
        </Field>
        <Field label="Teacher">
          <select value={form.teacherId} onChange={(event) => setForm((value) => ({ ...value, teacherId: event.target.value }))} required disabled={!form.subjectId}>
            <option value="">{form.subjectId ? "Select teacher" : "Select subject first"}</option>
            <SelectOptions rows={teachers} />
          </select>
        </Field>
        <Field label="Scheduled IN">
          <input type="time" value={form.scheduledIn} onChange={(event) => setForm((value) => ({ ...value, scheduledIn: event.target.value }))} required />
        </Field>
        <Field label="Scheduled OUT">
          <input type="time" value={form.scheduledOut} onChange={(event) => setForm((value) => ({ ...value, scheduledOut: event.target.value }))} required />
        </Field>
        <Field label="Status">
          <select value={form.status} onChange={(event) => setForm((value) => ({ ...value, status: event.target.value }))}>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="draft">Draft</option>
          </select>
        </Field>
      </div>
      <Field label="Notes">
        <textarea value={form.notes || ""} onChange={(event) => setForm((value) => ({ ...value, notes: event.target.value }))} />
      </Field>
      <Toggle label="Routine active" checked={form.active !== false} onChange={(value) => setForm((item) => ({ ...item, active: value }))} />
    </>
  );
}

function RoutineTableRow({ routine, master, onQuickSave, onEdit, onDuplicate, onDelete, dragProps }) {
  const [draft, setDraft] = useState(routine);
  const [saveState, setSaveState] = useState("saved");
  const readyRef = useRef(false);
  const lastSavedRef = useRef(normalizedRoutineValue(routine));
  const sections = (master.sections || []).filter((section) => !draft.classId || section.classId === draft.classId);
  const teachers = getTeachersForSubject(master.teachers || [], draft.subjectId);

  useEffect(() => {
    setDraft(routine);
    lastSavedRef.current = normalizedRoutineValue(routine);
    setSaveState("saved");
    readyRef.current = false;
    const timer = window.setTimeout(() => {
      readyRef.current = true;
    }, 50);
    return () => window.clearTimeout(timer);
  }, [routine]);

  useEffect(() => {
    if (!readyRef.current) return undefined;
    const nextValue = normalizedRoutineValue(draft);
    if (nextValue === lastSavedRef.current) return undefined;
    setSaveState("saving");
    const timer = window.setTimeout(async () => {
      try {
        await onQuickSave(draft);
        lastSavedRef.current = normalizedRoutineValue(draft);
        setSaveState("saved");
      } catch {
        setSaveState("error");
      }
    }, 700);
    return () => window.clearTimeout(timer);
  }, [draft, onQuickSave]);

  return (
    <tr draggable {...dragProps}>
      <td><GripVertical size={16} /></td>
      <td>
        <input
          value={Array.isArray(draft.days) ? draft.days.join(", ") : ""}
          onChange={(event) => setDraft((value) => ({ ...value, days: event.target.value.split(",").map((day) => day.trim()).filter(Boolean) }))}
        />
      </td>
      <td>
        <select value={draft.roomId || NA_ROOM_ID} onChange={(event) => updateRoomValue(setDraft, event.target.value)}>
          <option value={NA_ROOM_ID}>N/A</option>
          <SelectOptions rows={master.rooms || []} labelKey="roomNumber" />
        </select>
      </td>
      <td>
        <select value={draft.classId || ""} onChange={(event) => setDraft((value) => ({ ...value, classId: event.target.value, sectionId: "" }))}>
          <option value="">Class</option>
          <SelectOptions rows={master.classes || []} />
        </select>
      </td>
      <td>
        <select value={draft.sectionId || ""} onChange={(event) => setDraft((value) => ({ ...value, sectionId: event.target.value }))}>
          <option value="">Section</option>
          <SelectOptions rows={sections} />
        </select>
      </td>
      <td>
        <select value={draft.subjectId || ""} onChange={(event) => setDraft((value) => ({ ...value, subjectId: event.target.value, teacherId: "" }))}>
          <option value="">Subject</option>
          <SelectOptions rows={master.subjects || []} />
        </select>
      </td>
      <td>
        <select value={draft.teacherId || ""} onChange={(event) => setDraft((value) => ({ ...value, teacherId: event.target.value }))} disabled={!draft.subjectId}>
          <option value="">{draft.subjectId ? "Teacher" : "Subject first"}</option>
          <SelectOptions rows={teachers} />
        </select>
      </td>
      <td><input type="time" value={draft.scheduledIn || ""} onChange={(event) => setDraft((value) => ({ ...value, scheduledIn: event.target.value }))} /></td>
      <td><input type="time" value={draft.scheduledOut || ""} onChange={(event) => setDraft((value) => ({ ...value, scheduledOut: event.target.value }))} /></td>
      <td>
        <select value={draft.active === false ? "inactive" : draft.status || "active"} onChange={(event) => setDraft((value) => ({ ...value, status: event.target.value, active: event.target.value !== "inactive" }))}>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="draft">Draft</option>
          <option value="inactive">Inactive</option>
        </select>
      </td>
      <td className="table-actions routine-table-actions">
        <span className={`autosave-pill autosave-${saveState}`}>
          {saveState === "saving" ? "Saving..." : saveState === "error" ? "Retry edit" : "Saved"}
        </span>
        <button className="icon-button" type="button" onClick={() => onEdit(routine)}><Edit3 size={16} /></button>
        <button className="icon-button" type="button" onClick={() => onDuplicate(routine)}><Copy size={16} /></button>
        <button className="icon-button danger" type="button" onClick={() => onDelete(routine)}><Trash2 size={16} /></button>
      </td>
    </tr>
  );
}

export default function RoutineManager() {
  const [master, setMaster] = useState(null);
  const [routines, setRoutines] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [modal, setModal] = useState(null);
  const [copyModal, setCopyModal] = useState(null);
  const [roomModal, setRoomModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dragId = useRef(null);
  const fileInput = useRef(null);
  const { showToast } = useToast();

  async function load(forceMaster = false) {
    setLoading(true);
    try {
      const [nextMaster, nextRoutines] = await Promise.all([getMasterData(forceMaster), getRoutines()]);
      setMaster(nextMaster);
      setRoutines(nextRoutines);
    } catch (error) {
      showToast(error.message || "Unable to load routines", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filteredRoutines = useMemo(() => {
    return routines
      .filter((routine) => {
        if (filters.day && !routine.days?.includes(filters.day)) return false;
        if (filters.classId && routine.classId !== filters.classId) return false;
        if (filters.roomId && routine.roomId !== filters.roomId) return false;
        if (filters.teacherId && routine.teacherId !== filters.teacherId) return false;
        if (filters.subjectId && routine.subjectId !== filters.subjectId) return false;
        const haystack = `${routine.days?.join(" ")} ${routine.roomNumber} ${routine.className} ${routine.sectionName} ${routine.subjectName} ${routine.teacherName} ${routine.scheduledIn} ${routine.scheduledOut} ${routine.status}`.toLowerCase();
        return haystack.includes(search.toLowerCase());
      })
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
  }, [routines, filters, search]);

  function openForm(routine = null) {
    setModal({
      id: routine?.id || null,
      form: {
        ...emptyRoutine,
        ...(routine || {}),
        days: routine?.days || emptyRoutine.days
      }
    });
  }

  async function handleSave(event) {
    event.preventDefault();
    if (!modal.form.days?.length) {
      showToast("Select at least one day", "warning");
      return;
    }
    setSaving(true);
    try {
      await saveRoutine(enrichRoutine(modal.form, master), modal.id);
      showToast("Routine saved");
      setModal(null);
      await load();
    } catch (error) {
      showToast(error.message || "Unable to save routine", "error");
    } finally {
      setSaving(false);
    }
  }

  const handleQuickSave = useCallback(async (draft) => {
    await saveRoutine(enrichRoutine(draft, master), draft.id);
    setRoutines((current) =>
      current.map((routine) => (routine.id === draft.id ? { ...routine, ...enrichRoutine(draft, master) } : routine))
    );
  }, [master]);

  async function handleDuplicate(routine) {
    setSaving(true);
    try {
      await duplicateRoutine(routine);
      showToast("Routine duplicated");
      await load();
    } catch (error) {
      showToast(error.message || "Unable to duplicate routine", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteRoutine(confirm.id);
      showToast("Routine deleted");
      setConfirm(null);
      await load();
    } catch (error) {
      showToast(error.message || "Unable to delete routine", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDrop(targetId) {
    const sourceId = dragId.current;
    dragId.current = null;
    if (!sourceId || sourceId === targetId) return;
    const current = [...routines];
    const sourceIndex = current.findIndex((item) => item.id === sourceId);
    const targetIndex = current.findIndex((item) => item.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const [moved] = current.splice(sourceIndex, 1);
    current.splice(targetIndex, 0, moved);
    setRoutines(current);
    try {
      await reorderRoutines(current);
      showToast("Routine order saved");
    } catch (error) {
      showToast(error.message || "Unable to save order", "error");
      await load();
    }
  }

  async function handleCopyDays(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const count = await copyRoutinesBetweenDays(copyModal.sourceDay, copyModal.targetDay);
      showToast(`${count} routine${count === 1 ? "" : "s"} copied`);
      setCopyModal(null);
      await load();
    } catch (error) {
      showToast(error.message || "Unable to copy routines", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleImport(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      const rows = await readExcelFile(file);
      await importRoutines(rows);
      showToast("Routines imported");
      await load();
    } catch (error) {
      showToast(error.message || "Unable to import routine file", "error");
    } finally {
      event.target.value = "";
      setSaving(false);
    }
  }

  async function handleQuickRoomChange(event) {
    event.preventDefault();
    const targetRoom = master.rooms.find((room) => room.id === roomModal.targetRoomId);
    if (!roomModal.classId) {
      showToast("Select class for room change", "warning");
      return;
    }
    if (!targetRoom) {
      showToast("Select target room", "warning");
      return;
    }
    setSaving(true);
    try {
      const scope = roomModal.onlyFiltered ? filteredRoutines : routines;
      const count = await bulkChangeRoutineRoom({
        routines: scope.filter(
          (routine) => routine.classId === roomModal.classId && (!roomModal.day || routine.days?.includes(roomModal.day))
        ),
        sourceRoomId: roomModal.sourceRoomId,
        targetRoom
      });
      const className = master.classes.find((item) => item.id === roomModal.classId)?.name || "selected class";
      showToast(`${count} ${className} routine${count === 1 ? "" : "s"} moved to Room ${targetRoom.roomNumber}`);
      setRoomModal(null);
      await load();
    } catch (error) {
      showToast(error.message || "Unable to change room", "error");
    } finally {
      setSaving(false);
    }
  }

  if (loading || !master) {
    return (
      <section className="admin-page">
        <SkeletonList />
      </section>
    );
  }

  return (
    <section className="admin-page routine-manager">
      <div className="page-title-row">
        <div>
          <p>Admin</p>
          <h2>Routine Manager</h2>
        </div>
        <div className="toolbar-actions">
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => setRoomModal({ classId: filters.classId || "", sourceRoomId: "", targetRoomId: "", day: filters.day || "", onlyFiltered: true })}
          >
            <Repeat size={17} />Quick Room Change
          </button>
          <button className="btn btn-ghost" type="button" onClick={() => setCopyModal({ sourceDay: getDayName(), targetDay: "Monday" })}><Copy size={17} />Copy Day</button>
          <button className="btn btn-ghost" type="button" onClick={() => exportRowsToExcel(filteredRoutines.map(routineToExportRow), "pariksit-routines.xlsx", "Routines")}><Download size={17} />Export</button>
          <button className="btn btn-ghost" type="button" onClick={() => fileInput.current?.click()}><Upload size={17} />Import</button>
          <button className="btn btn-primary" type="button" onClick={() => openForm()}><Plus size={18} />Add Routine</button>
          <input ref={fileInput} className="hidden-input" type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} />
        </div>
      </div>
      <SearchBar value={search} onChange={setSearch} placeholder="Search day, class, room, teacher, subject, time or status" />
      <div className="filters-row">
        <select value={filters.day} onChange={(event) => setFilters((value) => ({ ...value, day: event.target.value }))}>
          <option value="">All days</option>
          {DAYS.map((day) => <option key={day} value={day}>{day}</option>)}
        </select>
        <select value={filters.classId} onChange={(event) => setFilters((value) => ({ ...value, classId: event.target.value }))}>
          <option value="">All classes</option>
          <SelectOptions rows={master.classes || []} />
        </select>
        <select value={filters.roomId} onChange={(event) => setFilters((value) => ({ ...value, roomId: event.target.value }))}>
          <option value="">All rooms</option>
          <option value={NA_ROOM_ID}>N/A</option>
          <SelectOptions rows={master.rooms || []} labelKey="roomNumber" />
        </select>
        <select value={filters.teacherId} onChange={(event) => setFilters((value) => ({ ...value, teacherId: event.target.value }))}>
          <option value="">All teachers</option>
          <SelectOptions rows={master.teachers || []} />
        </select>
        <select value={filters.subjectId} onChange={(event) => setFilters((value) => ({ ...value, subjectId: event.target.value }))}>
          <option value="">All subjects</option>
          <SelectOptions rows={master.subjects || []} />
        </select>
        <button className="btn btn-ghost" type="button" onClick={() => setFilters(emptyFilters)}>Clear</button>
      </div>

      {filteredRoutines.length ? (
        <>
          <div className="mobile-card-list">
            {filteredRoutines.map((routine) => (
              <article
                className="card manager-card"
                draggable
                key={routine.id}
                onDragStart={() => {
                  dragId.current = routine.id;
                }}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => handleDrop(routine.id)}
              >
                <GripVertical size={18} />
                <div>
                  <p className="eyebrow">{routine.days?.join(", ")}</p>
                  <h3>Room {routine.roomNumber} · {routine.className} - {routine.sectionName}</h3>
                  <p>{routine.subjectName} · {routine.teacherName}</p>
                  <p>{timeRangeLabel(routine.scheduledIn, routine.scheduledOut)}</p>
                  <StatusChip status={routine.active === false ? "Inactive" : routine.status || "Active"} />
                </div>
                <div className="card-actions">
                  <button className="icon-button" type="button" onClick={() => openForm(routine)}><Edit3 size={18} /></button>
                  <button className="icon-button" type="button" onClick={() => handleDuplicate(routine)}><Copy size={18} /></button>
                  <button className="icon-button danger" type="button" onClick={() => setConfirm(routine)}><Trash2 size={18} /></button>
                </div>
              </article>
            ))}
          </div>
          <div className="table-shell desktop-table">
            <table className="data-table editable-table">
              <thead>
                <tr>
                  <th />
                  <th>Day</th>
                  <th>Room</th>
                  <th>Class</th>
                  <th>Section</th>
                  <th>Subject</th>
                  <th>Teacher</th>
                  <th>Schedule IN</th>
                  <th>Schedule OUT</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoutines.map((routine) => (
                  <RoutineTableRow
                    key={routine.id}
                    routine={routine}
                    master={master}
                    onQuickSave={handleQuickSave}
                    onEdit={openForm}
                    onDuplicate={handleDuplicate}
                    onDelete={setConfirm}
                    dragProps={{
                      onDragStart: () => {
                        dragId.current = routine.id;
                      },
                      onDragOver: (event) => event.preventDefault(),
                      onDrop: () => handleDrop(routine.id)
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <EmptyState title="No routines found" text="Add routines or adjust filters." />
      )}

      <Modal open={Boolean(modal)} title={modal?.id ? "Edit Routine" : "Add Routine"} onClose={() => setModal(null)} size="lg">
        <form onSubmit={handleSave}>
          <RoutineForm form={modal?.form || emptyRoutine} setForm={(updater) => setModal((value) => ({ ...value, form: typeof updater === "function" ? updater(value.form) : updater }))} master={master} />
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : null}Save Routine</button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(copyModal)} title="Copy Routine Day" onClose={() => setCopyModal(null)}>
        <form className="form-grid" onSubmit={handleCopyDays}>
          <Field label="Copy from">
            <select value={copyModal?.sourceDay || ""} onChange={(event) => setCopyModal((value) => ({ ...value, sourceDay: event.target.value }))}>
              {DAYS.map((day) => <option key={day} value={day}>{day}</option>)}
            </select>
          </Field>
          <Field label="Copy to">
            <select value={copyModal?.targetDay || ""} onChange={(event) => setCopyModal((value) => ({ ...value, targetDay: event.target.value }))}>
              {DAYS.map((day) => <option key={day} value={day}>{day}</option>)}
            </select>
          </Field>
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setCopyModal(null)}>Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : null}Copy</button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(roomModal)} title="Quick Room Change" description="Move routines for one selected class from one room to another." onClose={() => setRoomModal(null)}>
        <form className="form-grid" onSubmit={handleQuickRoomChange}>
          <Field label="Class">
            <select value={roomModal?.classId || ""} onChange={(event) => setRoomModal((value) => ({ ...value, classId: event.target.value }))} required>
              <option value="">Select class</option>
              <SelectOptions rows={master.classes || []} />
            </select>
          </Field>
          <Field label="Change from room">
            <select value={roomModal?.sourceRoomId || ""} onChange={(event) => setRoomModal((value) => ({ ...value, sourceRoomId: event.target.value }))} required>
              <option value="">Select old room</option>
              <option value={NA_ROOM_ID}>N/A</option>
              <SelectOptions rows={master.rooms || []} labelKey="roomNumber" />
            </select>
          </Field>
          <Field label="Change to room">
            <select value={roomModal?.targetRoomId || ""} onChange={(event) => setRoomModal((value) => ({ ...value, targetRoomId: event.target.value }))} required>
              <option value="">Select new room</option>
              <SelectOptions rows={master.rooms || []} labelKey="roomNumber" />
            </select>
          </Field>
          <Field label="Day optional">
            <select value={roomModal?.day || ""} onChange={(event) => setRoomModal((value) => ({ ...value, day: event.target.value }))}>
              <option value="">All days</option>
              {DAYS.map((day) => <option key={day} value={day}>{day}</option>)}
            </select>
          </Field>
          <Toggle
            label="Apply only current filtered list"
            checked={roomModal?.onlyFiltered !== false}
            onChange={(value) => setRoomModal((item) => ({ ...item, onlyFiltered: value }))}
          />
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setRoomModal(null)}>Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : null}Apply Room Change</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Delete routine?"
        message={`Delete routine for Room ${confirm?.roomNumber || ""}?`}
        loading={saving}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDelete}
      />
    </section>
  );
}
